// Alberta Pharmacy Platform - Types

export interface User {
  id: number;
  email: string;
  username: string;
  full_name: string;
  role: UserRole;
  is_active: boolean;
  pharmacy_id?: number;
  created_at: string;
  updated_at?: string;
}

export interface Patient {
  id: number;
  phn?: string; // Personal Health Number for Alberta
  first_name: string;
  last_name: string;
  date_of_birth: string;
  phone?: string;
  address?: string;
  allergies?: string[];
  medications?: Medication[];
  created_at: string;
}

export interface Medication {
  id: number;
  name: string;
  generic_name?: string;
  strength: string;
  form: 'tablet' | 'capsule' | 'liquid' | 'injection';
  dosage: string;
  frequency: string;
  duration: string;
  indications: string[];
  contraindications?: string[];
  side_effects?: string[];
  inventory_count: number;
  ndc?: string; // National Drug Code
}

export interface Prescription {
  id: number;
  prescription_number: string;
  patient_id: number;
  patient: Patient;
  prescriber_id: number;
  prescriber: Prescriber;
  medication_id: number;
  medication: Medication;
  date_prescribed: string;
  date_filled?: string;
  expiry_date?: string;
  status: PrescriptionStatus;
  refills_remaining: number;
  refills_used: number;
  instructions: string;
  diagnosis?: string;
  pharmacy_id: number;
  processing_time?: number; // in seconds
  created_at: string;
  updated_at?: string;
}

export interface Prescriber {
  id: number;
  name: string;
  license_number: string;
  specialty: string;
  phone?: string;
  fax?: string;
  email?: string;
  is_active: boolean;
  college?: string; // College of Physicians & Surgeons of Alberta
  created_at: string;
}

export interface AssessmentTemplate {
  id: number;
  name: string;
  condition: string;
  template_type: TemplateType;
  fields: TemplateField[];
  is_active: boolean;
  created_by: number;
  created_at: string;
  updated_at?: string;
}

export interface TemplateField {
  id: number;
  field_name: string;
  field_type: FieldType;
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[]; // for select fields
  validation?: ValidationRule[];
  order: number;
}

export interface ClinicalAssessment {
  id: number;
  prescription_id: number;
  template_id: number;
  template: AssessmentTemplate;
  responses: AssessmentResponse[];
  assessment_date: string;
  assessed_by: number;
  notes?: string;
  created_at: string;
}

export interface AssessmentResponse {
  field_id: number;
  field: TemplateField;
  value: any;
  created_at: string;
}

export interface ProcessingMetrics {
  prescriptions_processed: number;
  average_processing_time: number; // in seconds
  error_rate: number; // percentage
  throughput_per_hour: number;
  time_saved: number; // in hours compared to manual
  date: string;
}

export interface Pharmacy {
  id: number;
  name: string;
  address: string;
  phone: string;
  fax?: string;
  email?: string;
  license_number: string;
  is_active: boolean;
  created_at: string;
}

export interface Document {
  id: number;
  prescription_id: number;
  document_type: DocumentType;
  file_path: string;
  file_name: string;
  generated_at: string;
  generated_by: number;
}

export interface ProcessingQueue {
  id: number;
  prescription_id: number;
  priority: QueuePriority;
  assigned_to?: number;
  status: QueueStatus;
  started_at?: string;
  completed_at?: string;
  estimated_completion?: string;
}

// Enums
export enum UserRole {
  PHARMACIST = 'pharmacist',
  PHARMACY_TECHNICIAN = 'pharmacy_technician',
  MANAGER = 'manager',
  ADMIN = 'admin',
}

export enum PrescriptionStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  ASSESSMENT_REQUIRED = 'assessment_required',
  READY_FOR_PICKUP = 'ready_for_pickup',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  REFILL_REQUESTED = 'refill_requested'
}

export enum TemplateType {
  HYPERTENSION_MONITORING = 'hypertension_monitoring',
  MEDICATION_REVIEW = 'medication_review',
  REFILL_ASSESSMENT = 'refill_assessment',
  PATIENT_EDUCATION = 'patient_education'
}

export enum FieldType {
  TEXT = 'text',
  NUMBER = 'number',
  SELECT = 'select',
  CHECKBOX = 'checkbox',
  CHECKBOX_GROUP = 'checkbox_group',
  DATE = 'date',
  TEXTAREA = 'textarea'
}

export enum QueuePriority {
  URGENT = 'urgent',
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low'
}

export enum QueueStatus {
  QUEUED = 'queued',
  IN_PROGRESS = 'in_progress',
  AWAITING_ASSESSMENT = 'awaiting_assessment',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

export enum DocumentType {
  PRESCRIPTION_LABEL = 'prescription_label',
  PATIENT_INFO_SHEET = 'patient_info_sheet',
  ASSESSMENT_REPORT = 'assessment_report',
  EDUCATION_MATERIAL = 'education_material'
}

export interface ValidationRule {
  type: 'required' | 'min' | 'max' | 'pattern';
  value?: any;
  message: string;
}

export interface TimeTracking {
  prescription_id: number;
  action: ProcessingAction;
  timestamp: string;
  duration?: number; // in seconds
  user_id: number;
}

export enum ProcessingAction {
  RECEIVED = 'received',
  ASSESSMENT_STARTED = 'assessment_started',
  ASSESSMENT_COMPLETED = 'assessment_completed',
  DOCUMENT_GENERATED = 'document_generated',
  PROCESSING_COMPLETED = 'processing_completed'
}

// ---------------------------------------------------------------------------
// DAP Wizard types
// ---------------------------------------------------------------------------

export enum EncounterType {
  NEW_DISPENSING = 'New Dispensing',
  REFILL = 'Refill',
  RENEWAL = 'Renewal',
  ADAPTATION = 'Adaptation',
  TRANSFER = 'Transfer',
  PRESCRIBING = 'Prescribing',
}

export type DURSeverity = 'CRITICAL' | 'WARNING' | 'INFO';

export interface DURFinding {
  id: string;
  severity: DURSeverity;
  title: string;
  message: string;
  acknowledged: boolean;
  override_reason?: string;
}

export interface ClinicalData {
  medication_id: number;
  brand_name: string;
  generic_name: string;
  strength: string;
  dosage_form: string;
  route: string;
  din: string;
  drug_class: string | null;
  therapeutic_category: string | null;
  drug_schedule: string | null;
  requires_triplicate: boolean;
  is_narcotic: boolean;
  is_controlled: boolean;
  is_high_alert: boolean;
  requires_special_authorization: boolean;
  automation_rules: AutomationRule[];
  required_checks: RequiredCheck[];
  counselling_items: CounsellingItem[];
}

export interface AutomationRule {
  id?: number;
  drug_class: string;
  condition: string;
  action_type: 'WARNING' | 'REQUIRED_CHECK' | 'COUNSELING' | 'AUTHORIZATION';
  title: string;
  message: string;
  ui_element: string;
  form_fields: string[];
  priority: number;
  is_required: boolean;
}

export interface RequiredCheck {
  name: string;
  label: string;
  required: boolean;
  target_range?: string;
  ui_element: string;
}

export interface CounsellingItem {
  item: string;
  required: boolean;
  category: string;
  checked?: boolean;
}

// A single drug entry in a multi-drug prescription
export interface DrugEntry {
  // Identity
  id: number | null;
  brandName: string;
  genericName: string;
  strength: string;
  dosageForm: string;
  route: string;
  din: string;
  drugClass: string | null;
  schedule: string | null;
  isHighAlert: boolean;
  isControlled: boolean;
  requiresTriplicate: boolean;
  // Rx-specific (per drug)
  qty: number;
  daysSupply: number;
  sig: string;
  genericSubAllowed: boolean;
  isManualDrug: boolean;
  // Clinical data loaded in DUR step
  clinicalData: ClinicalData | null;
}

// The assembled state flowing through the 7-step wizard
export interface WizardState {
  // Step 2 — shared Schedule I triplicate number, if any drug requires it
  triplicateNumber: string;
  // Step 1
  encounterType: EncounterType | null;

  // Step 2 — One or more drug entries
  drugs: DrugEntry[];

  // Step 3 — Patient Profile (anonymous)
  ageRange: string;
  sex: string;
  conditions: string[];
  drugAllergies: string[];

  // Step 4 — DUR (aggregated across all drugs)
  durFindings: DURFinding[];

  // Step 5 — Type-specific documentation
  typeSpecificData: Record<string, string>;

  // Step 6 — Counselling & Outcome (merged across all drugs)
  counsellingItems: CounsellingItem[];
  patientUnderstanding: string;
  outcome: string;
  followUpRequired: boolean;
  followUpTimeframe: string;
  additionalNotes: string;
}

// Alberta pharmacy directory entry (from imported OSM / ACP data)
export interface PharmacyDirectoryEntry {
  id: number;
  osm_id: string;
  name: string;
  address: string | null;
  city: string;
  province: string;
  postal_code: string | null;
  phone: string | null;
  website: string | null;
  latitude: string | null;
  longitude: string | null;
  is_active: boolean;
}
